import React, { useState } from 'react';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { Link, useNavigate } from 'react-router-dom';
import schema from '../form/schema.json';
import uiSchema from '../form/uiSchema.json';

// Custom theme for form styling
const customTheme = {
  widgets: {
    // Override the default range/slider widget for @rjsf/core
    RangeWidget: (props) => {
      const {
        value,
        readonly,
        disabled,
        onChange,
        options,
        schema,
        id,
        min,
        max,
        step,
        onBlur,
        onFocus,
        rawErrors = [],
      } = props;

      // Determine min/max/step from schema or options
      const sliderMin = typeof min !== "undefined" ? min : schema.minimum ?? 1;
      const sliderMax = typeof max !== "undefined" ? max : schema.maximum ?? 5;
      const sliderStep = typeof step !== "undefined" ? step : options.step ?? 1;
      const currentValue = typeof value === "undefined" ? (schema.default ?? sliderMin) : value;

      return (
        <div className="slider-widget" style={{padding: '0.5rem'}}>
          <div className="flex items-center gap-2">
            {/* <span className="text-xs text-muted">{sliderMin}</span> */}
            <input
              type="range"
              id={id}
              className="range"
              min={sliderMin}
              max={sliderMax}
              step={sliderStep}
              value={currentValue}
              disabled={disabled || readonly}
              onChange={e => onChange(Number(e.target.value))}
              onBlur={onBlur && ((event) => onBlur(id, event.target.value))}
              onFocus={onFocus && ((event) => onFocus(id, event.target.value))}
              style={{ flex: 1 }}
            />
            {/* <span className="text-xs text-muted">{sliderMax}</span> */}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem',padding: '0 0.5rem' }}>
            {Array.from({ length: (sliderMax - sliderMin + 1) }, (_, i) => sliderMin + i).map(n => (
              <span key={n} className="text-xs text-muted">{n}</span>
            ))}
          </div>
          <div className="text-sm text-muted mt-1">Selected: <span className="font-semibold text-primary">{currentValue}</span></div>
          {rawErrors.length > 0 && (
            <div className="form-error">{rawErrors.join(', ')}</div>
          )}
        </div>
      );
    }
  },
  templates: {
    FieldTemplate: ({ id, label, children, errors, help, description, hidden, required, displayLabel }) => {
      if (hidden) {
        return <div style={{ display: 'none' }}>{children}</div>;
      }
      return (
        <div className="form-control">
          {displayLabel && (
            <label className="form-label" htmlFor={id}>
              {label}
              {required && <span className="text-error">*</span>}
            </label>
          )}
          {description && (
            <div className="form-help mb-2">{description}</div>
          )}
          {children}
          {errors && (
            <div className="form-error">{errors}</div>
          )}
          {help && (
            <div className="form-help">{help}</div>
          )}
        </div>
      );
    },
    ObjectFieldTemplate: ({ properties, title, description }) => (
      <div className="space-y-4">
        {title && <h3 className="text-lg font-semibold text-primary mb-4">{title}</h3>}
        {description && <p className="text-secondary mb-4">{description}</p>}
        {properties.map(element => element.content)}
      </div>
    ),
  }
};

function MultiStepForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    visionLeadership: {},
    governanceEthics: {},
    dataInfrastructure: {},
    talentCulture: {},
    implementationContinuousImprovement: {}
  });

  const stepKeys = Object.keys(schema.properties);
  const steps = stepKeys.map((key) => ({
    key,
    title: schema.properties[key].title,
    schema: {
      type: 'object',
      title: schema.properties[key].title,
      properties: schema.properties[key].properties,
      required: schema.properties[key].required
    },
    uiSchema: uiSchema[key]
  }));

  const currentStepData = steps[currentStep];

  const handleNext = (data) => {
    const updatedFormData = {
      ...formData,
      [currentStepData.key]: data.formData
    };
    setFormData(updatedFormData);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/results', { state: { results: updatedFormData } });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen py-8">
      <div className="container">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="card card-lg">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-primary mb-4">AI Readiness Assessment</h1>
              <p className="text-secondary mb-4">
                Step {currentStep + 1} of {steps.length}
              </p>
              <div className="progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Step Indicators */}
            <div className="mb-8">
              <ul className="steps">
                {steps.map((step, index) => (
                  <li 
                    key={step.key} 
                    className={`step ${index <= currentStep ? 'active' : ''}`}
                  >
                    <div className="step-circle">
                      {index < currentStep ? '✓' : index + 1}
                    </div>
                    <div className="step-title">{step.title}</div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Form */}
            <div className="mb-6 rjsf">
              <Form
                schema={currentStepData.schema}
                uiSchema={currentStepData.uiSchema}
                formData={formData[currentStepData.key]}
                validator={validator}
                onSubmit={handleNext}
                onError={(errors) => console.log('Form errors:', errors)}
                templates={customTheme.templates}
                widgets={customTheme.widgets}
              >
                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                    className="btn btn-outline"
                  >
                    Previous
                  </button>
                  
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {currentStep === steps.length - 1 ? 'Submit' : 'Next'}
                  </button>
                </div>
              </Form>
            </div>

            {/* Back to Home Link */}
            <div className="text-center mt-6">
              <Link to="/" className="btn btn-ghost">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MultiStepForm;
